import { AlertCircle, Clock3, RefreshCw, Server } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { MetricCard } from '../components/ui/metric-card'
import { Button } from '../components/ui/button'
import { EmptyState } from '../components/ui/empty-state'
import {
  getDeploymentStatus,
  type DeploymentStatusColor,
  type DeploymentStatusEnvironment,
  type DeploymentStatusResponse,
  type DeploymentStatusService
} from '../lib/api/platformStatus'

const colorStyles: Record<DeploymentStatusColor, { dot: string; label: string }> = {
  Red: { dot: 'bg-rose-500 shadow-rose-500/30', label: 'Down' },
  Green: { dot: 'bg-emerald-500 shadow-emerald-500/30', label: 'Current' },
  Blue: { dot: 'bg-sky-500 shadow-sky-500/30', label: 'Commit mismatch' }
}

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '—'
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(new Date(value))
  } catch {
    return value
  }
}

const shortCommit = (value?: string | null) => value?.slice(0, 7) ?? 'unknown'

export const StatusPage = () => {
  const [status, setStatus] = useState<DeploymentStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadStatus = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }

    setError(null)

    try {
      const data = await getDeploymentStatus()
      setStatus(data)
    } catch (err) {
      console.error('Failed to load deployment status', err)
      setError('Could not load service status right now.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    void loadStatus()
  }, [loadStatus])

  const summary = useMemo(() => {
    const services = status?.environments.flatMap((environment) => environment.services) ?? []

    return {
      total: services.length,
      green: services.filter((service) => service.state === 'Green').length,
      blue: services.filter((service) => service.state === 'Blue').length,
      red: services.filter((service) => service.state === 'Red').length
    }
  }, [status])

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Platform</p>
          <h1 className="text-3xl font-semibold text-foreground">Service status</h1>
          <p className="text-sm text-muted-foreground">
            Red means down, green means the service is on the current shared commit for its environment, and blue means it is reachable on a different commit.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden items-center gap-2 rounded-xl border border-border/70 bg-card/70 px-3 py-2 text-xs text-muted-foreground md:flex">
            <Clock3 className="h-4 w-4" />
            Last check {formatDateTime(status?.checkedAtUtc)}
          </div>
          <Button variant="outline" onClick={() => void loadStatus(true)} disabled={refreshing}>
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard title="Services" value={summary.total} description="Configured local and staging targets." icon={Server} />
        <MetricCard title="Green" value={summary.green} description="Running on the reference commit." variant="success" />
        <MetricCard title="Blue" value={summary.blue} description="Running, but on a different commit." variant="accent" />
        <MetricCard title="Red" value={summary.red} description="Did not respond to the status probe." variant="warning" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Status legend</CardTitle>
          <CardDescription>Each environment is compared internally, so local services are compared to local peers and Railway staging services are compared to the staging set.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3">
          {(['Green', 'Blue', 'Red'] as DeploymentStatusColor[]).map((color) => (
            <div key={color} className="flex items-start gap-3 rounded-2xl border border-border/70 bg-card/50 p-4">
              <span className={`mt-1 h-3 w-3 rounded-full shadow-lg ${colorStyles[color].dot}`} />
              <div>
                <p className="text-sm font-semibold text-foreground">{colorStyles[color].label}</p>
                <p className="text-xs text-muted-foreground">
                  {color === 'Green' && 'The service is reachable and matches the environment reference commit.'}
                  {color === 'Blue' && 'The service is reachable, but it differs from the environment reference commit.'}
                  {color === 'Red' && 'The service did not return status metadata.'}
                </p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {error && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertCircle className="mt-0.5 h-5 w-5 text-rose-600" />
            <div>
              <p className="font-semibold text-rose-900">Status request failed</p>
              <p className="text-sm text-rose-800">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && !error && (!status || status.environments.length === 0) && (
        <Card>
          <CardContent>
            <EmptyState
              icon={Server}
              title="No services configured"
              description="Add deployment status URLs in the backend configuration so this page can compare local and Railway services."
            />
          </CardContent>
        </Card>
      )}

      {status?.environments.map((environment) => (
        <EnvironmentCard key={environment.id} environment={environment} />
      ))}
    </div>
  )
}

const EnvironmentCard = ({ environment }: { environment: DeploymentStatusEnvironment }) => (
  <Card>
    <CardHeader>
      <CardTitle>{environment.label}</CardTitle>
      <CardDescription>
        Reference commit {environment.referenceCommitSha ? shortCommit(environment.referenceCommitSha) : 'unavailable'}
      </CardDescription>
    </CardHeader>
    <CardContent className="grid gap-4 xl:grid-cols-2">
      {environment.services.map((service) => (
        <ServiceCard key={`${environment.id}-${service.id}-${service.url}`} service={service} />
      ))}
    </CardContent>
  </Card>
)

const ServiceCard = ({ service }: { service: DeploymentStatusService }) => {
  const tone = colorStyles[service.state]

  return (
    <div className="rounded-2xl border border-border/70 bg-card/50 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <span className={`h-3 w-3 rounded-full shadow-lg ${tone.dot}`} />
            <div>
              <h3 className="text-base font-semibold text-foreground">{service.label}</h3>
              <p className="text-xs text-muted-foreground">{tone.label}</p>
            </div>
          </div>
          <p className="mt-3 truncate font-mono text-xs text-muted-foreground">{service.url}</p>
        </div>

        <div className="rounded-xl border border-border/70 bg-background/70 px-3 py-2 text-right">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Commit</p>
          <p className="font-mono text-sm font-semibold text-foreground">{shortCommit(service.commitSha)}</p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <Detail label="Branch" value={service.branch ?? '—'} />
        <Detail label="Built" value={formatDateTime(service.buildTimeUtc)} />
        <Detail label="Source" value={service.commitSource ?? '—'} />
      </div>

      {service.message && (
        <p className="mt-3 rounded-xl border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          {service.message}
        </p>
      )}
    </div>
  )
}

const Detail = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
    <p className="mt-1 text-sm font-medium text-foreground">{value}</p>
  </div>
)
